import {
  IMPORT_FIELDS,
  formatMinor,
  type ImportColumnMap,
  type ImportField,
  type ImportOptions,
  type ImportPreview,
  type ImportRunResult,
} from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Pill,
  StatBlock,
  cn,
  type PillTone,
} from '@hvac/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  connectionKeys,
  fileToBase64,
  previewImport,
  runImport,
  suggestImport,
} from '../../shared/api/connection-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { importSentence } from './connection-text.js';
import { ConnectorMark } from './connector-mark.js';

/**
 * Import a spreadsheet of invoices from any CRM.
 *
 * Four steps a person can follow without help: drop the file; check which
 * column is which (already matched, from last time or from the headers and
 * the cells); see every row as it will land, totals and problems included,
 * before anything is saved; then bring it in. The layout is remembered by its
 * headers, so next month's export from the same CRM arrives matched.
 */

const MAX_BYTES = 3 * 1024 * 1024;

export const FIELD_INFO: Readonly<
  Record<ImportField, { label: string; help: string; required?: boolean }>
> = {
  invoiceNumber: { label: 'Invoice number', help: 'The invoice’s own number.', required: true },
  issuedOn: {
    label: 'Invoice date',
    help: 'When it was issued, not when it is due.',
    required: true,
  },
  customer: { label: 'Customer', help: 'Matched to a customer by name, or added.' },
  subtotal: { label: 'Subtotal', help: 'The amount before tax.' },
  tax: { label: 'Tax', help: 'The HST or GST charged.' },
  total: {
    label: 'Total',
    help: 'Including tax. Needed unless there is a subtotal.',
    required: true,
  },
  outstanding: { label: 'Balance owing', help: 'What is still owed; it feeds Receivables.' },
  status: { label: 'Status', help: 'Voided invoices are reversed; drafts are left out.' },
  jobNumber: { label: 'Job number', help: 'Ties the invoice to a job already in the books.' },
};

const SOURCE_LABEL = {
  saved: 'Remembered',
  name: 'Matched by header',
  shape: 'Matched by contents',
  ai: 'Suggested by AI',
} as const;
type Source = keyof typeof SOURCE_LABEL;

/** The required fields still without a column. */
export function missingFields(map: ImportColumnMap): ImportField[] {
  const missing: ImportField[] = [];
  if (map.invoiceNumber === undefined) missing.push('invoiceNumber');
  if (map.issuedOn === undefined) missing.push('issuedOn');
  if (map.total === undefined && map.subtotal === undefined) missing.push('total');
  return missing;
}

/** "Workiz invoices 2026-10.csv" → "Workiz invoices": a name for the layout. */
export function layoutName(fileName: string): string {
  const base = fileName
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\d{4}([ .]?\d{1,2}){0,2}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return (base === '' ? 'Spreadsheet' : base).slice(0, 80);
}

type Step = 'file' | 'columns' | 'check' | 'done';

export function ImportPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [columnMap, setColumnMap] = useState<ImportColumnMap>({});
  const [sources, setSources] = useState<Partial<Record<ImportField, Source>>>({});
  const [options, setOptions] = useState<ImportOptions>({ dateOrder: 'mdy' });
  const [name, setName] = useState('');
  const [checked, setChecked] = useState<ImportRunResult | null>(null);
  const [done, setDone] = useState<ImportRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This page takes its own drops; the app-wide "drop to sort" stands aside.
  useEffect(() => {
    document.body.dataset['ownDrops'] = 'true';
    return () => {
      delete document.body.dataset['ownDrops'];
    };
  }, []);

  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof Error ? caught.message : fallback);

  const read = useMutation({
    mutationFn: async (picked: File) => {
      if (picked.size > MAX_BYTES) {
        throw new Error(
          'That file is over 3 MB. Export a shorter period (a quarter or a year) and drop it in again.',
        );
      }
      const base64 = await fileToBase64(picked);
      const result = await previewImport(organizationId, picked.name, base64);
      return { picked, base64, result };
    },
    onSuccess: ({ picked, base64, result }) => {
      setError(null);
      setFile({ name: picked.name, base64 });
      setPreview(result);
      setColumnMap(result.suggestion.columnMap);
      setSources(result.suggestion.sources);
      setOptions(result.suggestion.options);
      setName(result.profile?.name ?? layoutName(picked.name));
      setStep('columns');
    },
    onError: (caught) => fail(caught, 'That file could not be read.'),
  });

  const suggest = useMutation({
    mutationFn: () => {
      if (preview === null) throw new Error('Choose a file first.');
      return suggestImport(organizationId, preview.headers, preview.sample);
    },
    onSuccess: ({ columnMap: suggested }) => {
      setError(null);
      const taken = new Set(Object.values(columnMap));
      const next = { ...columnMap };
      const nextSources = { ...sources };
      for (const field of IMPORT_FIELDS) {
        const header = suggested[field];
        // The AI fills gaps; it never overrides what the person or the
        // matcher already placed.
        if (header === undefined || next[field] !== undefined || taken.has(header)) continue;
        next[field] = header;
        nextSources[field] = 'ai';
        taken.add(header);
      }
      setColumnMap(next);
      setSources(nextSources);
    },
    onError: (caught) => fail(caught, 'The AI could not look at the columns.'),
  });

  const submit = useMutation({
    mutationFn: (dryRun: boolean) => {
      if (file === null) throw new Error('Choose a file first.');
      return runImport(organizationId, {
        fileName: file.name,
        contentBase64: file.base64,
        name: name.trim() === '' ? layoutName(file.name) : name.trim(),
        columnMap,
        options,
        dryRun,
      });
    },
    onSuccess: (result, dryRun) => {
      setError(null);
      if (dryRun) {
        setChecked(result);
        setStep('check');
      } else {
        setDone(result);
        setStep('done');
        void queryClient.invalidateQueries();
        void queryClient.invalidateQueries({ queryKey: connectionKeys.all(organizationId) });
      }
    },
    onError: (caught) => fail(caught, 'The import did not run.'),
  });

  const startOver = () => {
    setStep('file');
    setFile(null);
    setPreview(null);
    setChecked(null);
    setDone(null);
    setError(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/connections" className="text-sm text-content-muted hover:text-content">
          ← Connections
        </Link>
        <PageHeader
          className="mt-2"
          title="Import a spreadsheet"
          description="Invoices exported from any CRM become draft income, at the tax that was charged. Nothing is saved until you have seen every row."
        />
      </div>

      <Steps current={step} />

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      {step === 'file' ? (
        <FileStep onFile={(picked) => read.mutate(picked)} reading={read.isPending} />
      ) : null}

      {step === 'columns' && preview !== null && file !== null ? (
        <ColumnsStep
          fileName={file.name}
          preview={preview}
          columnMap={columnMap}
          sources={sources}
          options={options}
          name={name}
          onName={setName}
          onOptions={setOptions}
          onChange={(field, header) => {
            // A column fills one field: moving it here empties where it was.
            const cleared = new Set<ImportField>([field]);
            for (const other of IMPORT_FIELDS) {
              if (header !== null && columnMap[other] === header) cleared.add(other);
            }
            const next: ImportColumnMap = Object.fromEntries(
              Object.entries(columnMap).filter(([key]) => !cleared.has(key as ImportField)),
            );
            if (header !== null) next[field] = header;
            setColumnMap(next);
            setSources(
              Object.fromEntries(
                Object.entries(sources).filter(([key]) => !cleared.has(key as ImportField)),
              ),
            );
          }}
          onSuggest={() => suggest.mutate()}
          suggesting={suggest.isPending}
          onBack={startOver}
          onCheck={() => submit.mutate(true)}
          checking={submit.isPending}
        />
      ) : null}

      {step === 'check' && checked !== null ? (
        <CheckStep
          result={checked}
          onBack={() => setStep('columns')}
          onImport={() => submit.mutate(false)}
          importing={submit.isPending}
        />
      ) : null}

      {step === 'done' && done !== null ? (
        <DoneStep
          result={done}
          onAnother={startOver}
          onReview={() => void navigate({ to: '/review' })}
          onConnections={() => void navigate({ to: '/connections' })}
        />
      ) : null}
    </div>
  );
}

function Steps({ current }: { current: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'file', label: 'Choose the file' },
    { key: 'columns', label: 'Match the columns' },
    { key: 'check', label: 'Check every row' },
    { key: 'done', label: 'Bring it in' },
  ];
  const at = steps.findIndex((entry) => entry.key === current);
  return (
    <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {steps.map((entry, index) => (
        <li
          key={entry.key}
          className={cn(
            'flex items-center gap-2.5 rounded-[var(--radius-control)] border px-3 py-2.5 text-sm',
            index === at
              ? 'border-brand-500/40 bg-brand-50 font-medium text-content dark:bg-brand-500/10'
              : index < at
                ? 'border-border bg-surface text-content-muted'
                : 'border-border border-dashed text-content-subtle',
          )}
        >
          <span
            className={cn(
              'tabular flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
              index < at
                ? 'bg-success text-white'
                : index === at
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-sunken text-content-subtle',
            )}
          >
            {index < at ? '✓' : index + 1}
          </span>
          <span className="truncate">{entry.label}</span>
        </li>
      ))}
    </ol>
  );
}

function FileStep({ onFile, reading }: { onFile: (file: File) => void; reading: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <Card>
      <CardBody className="flex flex-col gap-5">
        <button
          type="button"
          disabled={reading}
          onClick={() => input.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setOver(false);
            const dropped = event.dataTransfer.files[0];
            if (dropped !== undefined) onFile(dropped);
          }}
          className={cn(
            'flex flex-col items-center gap-3 rounded-[var(--radius-surface)] border-2 border-dashed px-6 py-14 text-center',
            'transition-colors duration-[var(--duration-quick)]',
            over
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
              : 'border-border-strong bg-surface-sunken/40 hover:border-brand-400 hover:bg-brand-50/50 dark:hover:bg-brand-500/[0.06]',
          )}
        >
          <ConnectorMark kind="spreadsheet" size="lg" />
          <span className="text-base font-semibold text-content">
            {reading ? 'Reading the file…' : 'Drop the invoice export here, or choose it'}
          </span>
          <span className="text-sm text-content-muted">
            CSV or Excel (.xlsx), up to 5,000 invoices
          </span>
        </button>
        <input
          ref={input}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked !== undefined) onFile(picked);
            event.target.value = '';
          }}
        />
        <div className="grid gap-4 text-sm sm:grid-cols-3">
          <Tip title="Where to find it">
            In most CRMs: Invoices (or Reports), filter to the period, then Export to CSV or Excel.
          </Tip>
          <Tip title="One line per invoice">
            An invoice list, not line items. If the export repeats an invoice per item, the first
            line is used.
          </Tip>
          <Tip title="Safe to repeat">
            Importing the same file twice changes nothing. A later export updates the drafts and
            reverses voids.
          </Tip>
        </div>
      </CardBody>
    </Card>
  );
}

function Tip({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-medium text-content">{title}</p>
      <p className="mt-0.5 leading-relaxed text-content-muted">{children}</p>
    </div>
  );
}

const selectClass =
  'w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm';

function ColumnsStep({
  fileName,
  preview,
  columnMap,
  sources,
  options,
  name,
  onName,
  onOptions,
  onChange,
  onSuggest,
  suggesting,
  onBack,
  onCheck,
  checking,
}: {
  fileName: string;
  preview: ImportPreview;
  columnMap: ImportColumnMap;
  sources: Partial<Record<ImportField, Source>>;
  options: ImportOptions;
  name: string;
  onName: (value: string) => void;
  onOptions: (value: ImportOptions) => void;
  onChange: (field: ImportField, header: string | null) => void;
  onSuggest: () => void;
  suggesting: boolean;
  onBack: () => void;
  onCheck: () => void;
  checking: boolean;
}) {
  const missing = missingFields(columnMap);
  const samples = (header: string | undefined): string[] => {
    if (header === undefined) return [];
    const index = preview.headers.indexOf(header);
    return preview.sample
      .map((row) => row[index] ?? '')
      .filter((value) => value !== '')
      .slice(0, 3);
  };
  const usesSlashes =
    columnMap.issuedOn !== undefined &&
    samples(columnMap.issuedOn).some((value) => /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}/.test(value));

  return (
    <>
      <Card>
        <CardBody className="flex flex-wrap items-center gap-4">
          <ConnectorMark kind="spreadsheet" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-content">{fileName}</p>
            <p className="text-sm text-content-muted">
              {preview.rowCount.toLocaleString('en-CA')} rows, {preview.headers.length} columns
              {preview.sheetName === null ? '' : `, from the sheet “${preview.sheetName}”`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack}>
            Choose another file
          </Button>
        </CardBody>
      </Card>

      {preview.profile === null ? null : (
        <Alert tone="info" title={`This is “${preview.profile.name}”`}>
          The columns are matched the way you left them last time. Check them and carry on.
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Which column is which"
          description="Each field is matched already where the file made it clear. Change any that are wrong; leave out what the file does not have."
          actions={
            missing.length > 0 && preview.aiAvailable ? (
              <Button variant="secondary" size="sm" onClick={onSuggest} isLoading={suggesting}>
                Ask AI to match the rest
              </Button>
            ) : undefined
          }
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                  <th className="w-[30%] px-5 py-3 font-semibold">Field</th>
                  <th className="w-[30%] px-5 py-3 font-semibold">Column in the file</th>
                  <th className="px-5 py-3 font-semibold">What it holds</th>
                </tr>
              </thead>
              <tbody>
                {IMPORT_FIELDS.map((field) => {
                  const info = FIELD_INFO[field];
                  const header = columnMap[field];
                  const source = sources[field];
                  const needed = missing.includes(field);
                  return (
                    <tr
                      key={field}
                      className={cn(
                        'border-t border-border align-top',
                        needed && 'bg-warning-surface/40',
                      )}
                    >
                      <td className="px-5 py-3.5">
                        <p className="flex items-center gap-2 font-medium text-content">
                          {info.label}
                          {info.required === true ? (
                            <span className="text-xs font-normal text-content-subtle">
                              required
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-0.5 text-xs text-content-muted">{info.help}</p>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          aria-label={`Column for ${info.label}`}
                          value={header ?? ''}
                          onChange={(event) =>
                            onChange(field, event.target.value === '' ? null : event.target.value)
                          }
                          className={cn(selectClass, needed && 'border-warning')}
                        >
                          <option value="">Not in this file</option>
                          {preview.headers.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        {source === undefined || header === undefined ? null : (
                          <p className="mt-1.5">
                            <Pill tone={source === 'ai' ? 'info' : 'neutral'}>
                              {SOURCE_LABEL[source]}
                            </Pill>
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {header === undefined ? (
                          <span className="text-content-subtle">–</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {samples(header).map((value, index) => (
                              <span
                                key={`${value}-${String(index)}`}
                                className="tabular max-w-48 truncate rounded-md bg-surface-sunken px-2 py-0.5 text-xs text-content-muted"
                              >
                                {value}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Name this layout"
            hint="Next time a file with these columns arrives, it is matched this way."
          >
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={name}
                maxLength={80}
                onChange={(event) => onName(event.target.value)}
              />
            )}
          </Field>
          {usesSlashes ? (
            <Field
              label="The dates are written"
              hint={
                preview.suggestion.dateOrderUncertain
                  ? 'The file could be read either way. Check against an invoice you know.'
                  : 'Worked out from the file.'
              }
            >
              {({ inputProps }) => (
                <select
                  {...inputProps}
                  value={options.dateOrder}
                  onChange={(event) =>
                    onOptions({ dateOrder: event.target.value === 'dmy' ? 'dmy' : 'mdy' })
                  }
                  className={selectClass}
                >
                  <option value="mdy">Month first (06/03/2026 is June 3)</option>
                  <option value="dmy">Day first (06/03/2026 is March 6)</option>
                </select>
              )}
            </Field>
          ) : null}
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onCheck} isLoading={checking} disabled={missing.length > 0}>
          Check every row
        </Button>
        {missing.length > 0 ? (
          <span className="text-sm text-content-muted">
            Still needed: {missing.map((field) => FIELD_INFO[field].label.toLowerCase()).join(', ')}
            .
          </span>
        ) : (
          <span className="text-sm text-content-muted">Nothing is saved yet.</span>
        )}
      </div>
    </>
  );
}

function rowStatus(row: ImportRunResult['preview'][number]): { tone: PillTone; label: string } {
  if (row.problem !== null) return { tone: 'warning', label: 'Not used' };
  if (row.voided) return { tone: 'neutral', label: 'Void' };
  if (row.outstandingMinor === 0) return { tone: 'success', label: 'Paid' };
  return { tone: 'info', label: 'Ready' };
}

const money = (minor: number | null) => (minor === null ? '–' : formatMinor(String(minor)));

const day = (iso: string | null) =>
  iso === null
    ? '–'
    : new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

function CheckStep({
  result,
  onBack,
  onImport,
  importing,
}: {
  result: ImportRunResult;
  onBack: () => void;
  onImport: () => void;
  importing: boolean;
}) {
  const ready = result.preview.filter((row) => row.problem === null && !row.voided).length;
  const voids = result.preview.filter((row) => row.problem === null && row.voided).length;
  const usable = result.rows - result.skipped;
  const [showAll, setShowAll] = useState(false);
  const shown = result.preview.slice(0, showAll ? 500 : 25);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatBlock
          label="Invoices"
          value={ready.toLocaleString('en-CA')}
          hint={
            voids > 0 ? `and ${String(voids)} void${voids === 1 ? '' : 's'}` : 'ready to bring in'
          }
        />
        <StatBlock
          label="Invoiced"
          value={formatMinor(String(result.totals.invoicedMinor))}
          hint="tax included"
        />
        <StatBlock
          label="Tax charged"
          value={formatMinor(String(result.totals.taxMinor))}
          hint="as the file states it"
        />
        <StatBlock
          label="Period"
          value={
            result.totals.from === null ? '–' : day(result.totals.from).replace(/, \d{4}$/, '')
          }
          hint={
            result.totals.through === null
              ? 'no dated invoices'
              : `to ${day(result.totals.through)}`
          }
        />
      </div>

      {result.skipped === 0 ? null : (
        <Alert
          tone="warning"
          title={`${String(result.skipped)} row${result.skipped === 1 ? '' : 's'} will not be used`}
        >
          <ul className="mt-1 flex flex-col gap-0.5">
            {result.problems.slice(0, 6).map((problem) => (
              <li key={`${String(problem.row)}-${problem.problem}`}>
                {problem.row === null ? '' : `Row ${String(problem.row)}: `}
                {problem.problem}
              </li>
            ))}
            {result.problems.length > 6 ? (
              <li className="text-content-muted">
                and {result.problems.length - 6} more, marked below.
              </li>
            ) : null}
          </ul>
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Every row, as it will land"
          description="Read a few against the CRM. The tax is what the file says; an amount whose tax is not a plain Canadian rate is brought in at its total and waits in Review."
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                  <th className="px-4 py-3 font-semibold">Row</th>
                  <th className="px-4 py-3 font-semibold">Invoice</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 text-right font-semibold">Total</th>
                  <th className="px-4 py-3 text-right font-semibold">Tax</th>
                  <th className="px-4 py-3 text-right font-semibold">Owing</th>
                  <th className="px-4 py-3 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const status = rowStatus(row);
                  return (
                    <tr
                      key={row.row}
                      className={cn(
                        'border-t border-border',
                        row.problem !== null && 'text-content-subtle',
                      )}
                    >
                      <td className="tabular px-4 py-2.5 text-content-subtle">{row.row}</td>
                      <td className="px-4 py-2.5 font-medium">{row.invoiceNumber ?? '–'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">{day(row.issuedOn)}</td>
                      <td className="max-w-56 truncate px-4 py-2.5">{row.customer ?? '–'}</td>
                      <td className="tabular px-4 py-2.5 text-right">{money(row.totalMinor)}</td>
                      <td className="tabular px-4 py-2.5 text-right">{money(row.taxMinor)}</td>
                      <td className="tabular px-4 py-2.5 text-right">
                        {money(row.outstandingMinor)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span title={row.problem ?? undefined}>
                          <Pill tone={status.tone}>{status.label}</Pill>
                        </span>
                        {row.problem === null ? null : (
                          <p className="mt-1 max-w-64 text-xs text-content-muted">{row.problem}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {result.preview.length > 25 && !showAll ? (
            <div className="border-t border-border px-4 py-3">
              <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                {result.preview.length > 500
                  ? 'Show the first 500'
                  : `Show all ${result.preview.length.toLocaleString('en-CA')}`}
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onImport} isLoading={importing} disabled={usable === 0}>
          Bring in {usable.toLocaleString('en-CA')} invoice{usable === 1 ? '' : 's'}
        </Button>
        <Button variant="ghost" onClick={onBack}>
          Back to the columns
        </Button>
        <span className="text-sm text-content-muted">
          They arrive as drafts: nothing is signed until a person signs it.
        </span>
      </div>
    </>
  );
}

function DoneStep({
  result,
  onAnother,
  onReview,
  onConnections,
}: {
  result: ImportRunResult;
  onAnother: () => void;
  onReview: () => void;
  onConnections: () => void;
}) {
  return (
    <>
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-br from-success/10 to-transparent px-6 py-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-success">Imported</p>
          <p className="mt-2 max-w-2xl text-xl font-semibold leading-snug text-content">
            {importSentence(result)}
          </p>
        </div>
      </Card>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatBlock label="Added" value={String(result.created)} hint="new income" />
        <StatBlock label="Matched" value={String(result.linked)} hint="already in the books" />
        <StatBlock label="Refreshed" value={String(result.updated)} hint="drafts updated" />
        <StatBlock label="Voided" value={String(result.voided)} hint="reversed" />
        <StatBlock
          label="Tax to check"
          value={String(result.flagged)}
          hint={result.flagged === 0 ? 'all plain' : 'in Review'}
          {...(result.flagged > 0 ? { tone: 'danger' as const } : {})}
        />
        <StatBlock label="Not used" value={String(result.skipped)} hint="rows" />
      </div>
      <div className="flex flex-wrap gap-3">
        {result.flagged > 0 ? <Button onClick={onReview}>Check the tax in Review</Button> : null}
        <Button variant={result.flagged > 0 ? 'secondary' : 'primary'} onClick={onConnections}>
          Back to Connections
        </Button>
        <Button variant="ghost" onClick={onAnother}>
          Import another file
        </Button>
      </div>
    </>
  );
}
